import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useFinancialItem } from "../../contexts/FinancialItemContext";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import { Heading, Subheading } from "../../design/heading";
import { Divider } from "../../design/divider";
import DeleteFinancialItemButton from "./components/DeleteFinancialItemButton";
import LoadingView from "../../components/LoadingView";
import FinancialItemFilesList from "./components/Files/FinancialItemFilesList";
import ErrorView from "../../components/ErrorView";
import Breadcrumbs from "../../components/Breadcrumbs";
import FinancialItemButton from "../../components/FinancialItemButton";
import FinancialItemCategoryBadge from "../../components/Badges/FinancialItemCategoryBadge";

const FinancialItemDetailPage: React.FC = () => {
  const { financialItemId } = useParams<{ financialItemId: string }>();
  const { financialItem, setSelectedFinancialItem, error, loading } =
    useFinancialItem();

  useEffect(() => {
    if (financialItemId) {
      setSelectedFinancialItem(financialItemId);
    }
  }, [financialItemId]);

  if (loading.details) {
    return <LoadingView />;
  }

  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!financialItem) {
    return <ErrorView error={new Error("Financial Item not found")} />;
  }

  return (
    <div>
      <Breadcrumbs
        items={[
          { label: "Property Division", href: "/property-division" },
          {
            label: financialItem.name,
            href: `/financial-item/${financialItem._id}`,
          },
        ]}
      />

      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex flex-col w-full gap-6">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <Heading>{financialItem.name}</Heading>
              <div className="flex gap-2">
                <FinancialItemButton financialItem={financialItem} />
                <DeleteFinancialItemButton
                  financialItemId={financialItem._id}
                />
              </div>
            </div>
          </div>
          <Divider className="mt-2" />
          <DescriptionList>
            <DescriptionTerm>Category</DescriptionTerm>
            <DescriptionDetails>
              <FinancialItemCategoryBadge category={financialItem.category} />
            </DescriptionDetails>
            <DescriptionTerm>Value</DescriptionTerm>
            <DescriptionDetails>
              <span
                className={
                  financialItem.category === "asset" ? "" : "text-red-500"
                }
              >
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(financialItem.amount)}
              </span>
            </DescriptionDetails>
            {financialItem.associatedDebtAmount && (
              <>
                <DescriptionTerm>Associated Debt</DescriptionTerm>
                <DescriptionDetails>
                  <span className={"text-red-500"}>
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(financialItem.associatedDebtAmount)}
                  </span>
                </DescriptionDetails>
                <DescriptionTerm>Equity</DescriptionTerm>
                <DescriptionDetails>
                  <span
                    className={
                      financialItem.category === "asset" ? "" : "text-red-500"
                    }
                  >
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(
                      financialItem.amount - financialItem.associatedDebtAmount,
                    )}
                  </span>
                </DescriptionDetails>
              </>
            )}
            <DescriptionTerm>Your Ownership Percentage</DescriptionTerm>
            <DescriptionDetails>
              {financialItem.ownershipPercentage}%
            </DescriptionDetails>
          </DescriptionList>
          <Subheading>Supporting Files</Subheading>
          <FinancialItemFilesList financialItemId={financialItem._id} />
        </div>
      </div>
    </div>
  );
};

export default FinancialItemDetailPage;
